import { useCallback, useMemo, useRef, useState } from 'react';
import { getRowError } from '../../services/form-helpers';

const prefix = '³He';

/**
 * Debounce helper that returns a promise resolving with the debounced result.
 * Each new call cancels the previous pending timer.
 */
function makeDebouncedValidator(
  handler: (...args: any[]) => Promise<boolean | string>,
  wait: number,
) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let counter = 0;

  return (...args: any[]): Promise<boolean | string> => {
    if (timer) clearTimeout(timer);
    const callId = ++counter;

    return new Promise((resolve) => {
      timer = setTimeout(async () => {
        try {
          const result = await handler(...args);
          // Only resolve if this is still the latest call
          if (callId === counter) {
            resolve(result);
          } else {
            // Stale call – treat as passing so it doesn't block
            resolve(true);
          }
        } catch {
          if (callId === counter) {
            resolve(false);
          } else {
            resolve(true);
          }
        }
      }, wait);
    });
  };
}

const useValidation = ({field, validation, getValues, data, rowIdentity, isUnique, errors }) => {
  const [isValidating, setIsValidating] = useState(false);

  // Keep stable references to debounced wrappers across renders
  const debouncedRef = useRef<Record<string, (...args: any[]) => Promise<boolean | string>>>({});

  const customValidation = useMemo(() => {
    let c = Object.keys(validation || []).reduce((obj, current, idx) => {
      const entry = validation[current];
      const isAsync = entry.async === true;

      if (isAsync) {
        // Create a debounced wrapper once per validator key
        if (!debouncedRef.current[current]) {
          const debounceMs = entry.debounce ?? 300;
          debouncedRef.current[current] = makeDebouncedValidator(
            (val, gv, d, ri) => entry.handler(val, gv, d, ri),
            debounceMs,
          );
        }

        const debouncedFn = debouncedRef.current[current];

        obj[current] = async (val) => {
          setIsValidating(true);
          try {
            const result = await debouncedFn(val, getValues, data, rowIdentity);
            return result;
          } finally {
            setIsValidating(false);
          }
        };
      } else {
        obj[current] = (val) => {
          return validation[current].handler(val, getValues, data, rowIdentity);
        };
      }

      return obj;
    }, {});
    if (field.unique) {
      c['unique'] = isUnique;
    }
    return c;
  }, [validation]);

  const customValidatorNames = useMemo(() => {
    if (validation) return Object.keys(validation);
  }, [validation]);

  const rowError = getRowError(errors, field.name, rowIdentity);

  return [customValidation, customValidatorNames, rowError, isValidating];
};

export { useValidation };
